import { SuggestionOutput } from '@allocab/allocab-commons-shared-types';
import { TripOutput } from '@allocab/allocab-passenger-shared-types';

import { useAnonymousAskUpdate } from '../../../../hooks/useAnonymousAskUpdate';
import { useAnonymousConfirmUpdate } from '../../../../hooks/useAnonymousConfirmUpdate';
import { ToastManager } from '../../../../services/toast/utils';
import { t } from '../../../../services/translation/translation';
import { formatPrice } from '../../../../utils/trip';
import { DiffView } from '../../../atomic/DiffView';
import { GradientButton } from '../../../button/GradientButton';
import { SecondaryButton } from '../../../button/SecondaryButton';
import { ModalTitle } from '../../../Modal';
import { Spinner } from '../../../Spinner';
import { SuggestionsInput } from '../../SuggestionsInput';

type Props = {
  period: 'changeStartAddress' | 'changeEndAddress';
  trip: TripOutput;
  jwtToken: string;
  onClose: () => void;
};

export const UpdateAnonymousTripAddress = ({ period, jwtToken, trip, onClose }: Props) => {
  const {
    data: anonymousAskUpdateData,
    mutate: anonymousAskUpdate,
    isLoading: isAsking,
    reset,
  } = useAnonymousAskUpdate();

  const { mutate: confirmUpdate, isLoading: isConfirming } = useAnonymousConfirmUpdate(jwtToken);

  const handleSelectAddress = (address: SuggestionOutput | undefined) => {
    anonymousAskUpdate(
      period === 'changeStartAddress'
        ? { newStartAddress: address, key: trip.key, jwtToken: jwtToken }
        : { newEndAddress: address, key: trip.key, jwtToken: jwtToken },
    );
  };

  const handleConfirmAddress = () => {
    confirmUpdate(
      { key: trip.key, id: anonymousAskUpdateData?.id, jwtToken: jwtToken },
      {
        onSuccess: () => {
          ToastManager.show({ severity: 'success', message: t('update-trip.modal.success') });
          onClose();
        },
      },
    );
  };

  return (
    <div>
      <ModalTitle
        title={
          period === 'changeStartAddress'
            ? t('update-trip.modal.start-address-button')
            : t('update-trip.modal.end-address-button')
        }
      />
      {!anonymousAskUpdateData ? (
        <div className="flex flex-col space-y-3 justify-center mt-5 items-center">
          <div>{t('update-trip.modal.address-subtitle')}</div>
          <SuggestionsInput onSelect={e => handleSelectAddress(e)} className="self-stretch" />
          {isAsking ? <Spinner className="ml-5" /> : null}
        </div>
      ) : (
        <div>
          <DiffView
            newValue={period === 'changeStartAddress' ? anonymousAskUpdateData.newStart : anonymousAskUpdateData.newEnd}
            oldValue={
              period === 'changeStartAddress' ? trip.resume.start.formattedAddress : trip.resume.end.formattedAddress
            }
          />
          {anonymousAskUpdateData.requirePriceUpdate === true && anonymousAskUpdateData.newPrice ? (
            <div className="text-center mt-5 font-bold">
              {t('trip-screen.task-desc-edit-price', {
                newPrice: formatPrice(
                  anonymousAskUpdateData.newPrice.amountWithTax,
                  anonymousAskUpdateData.newPrice.currency,
                ),
                oldPrice: formatPrice(trip.resume.price.amountWithTax, trip.resume.price.currency),
              })}
            </div>
          ) : null}
          <div className="text-center mt-3">{t('trip-screen.asking-agreement-details')}</div>
          <div className="flex justify-center gap-5">
            <SecondaryButton onClick={() => reset()} className="w-full space-y-3 mt-2">
              {t('common.cancel')}
            </SecondaryButton>
            <GradientButton
              isLoading={isConfirming}
              onClick={() => handleConfirmAddress()}
              className="w-full space-y-3 mt-2"
            >
              {t('common.confirm')}
            </GradientButton>
          </div>
        </div>
      )}
    </div>
  );
};
